import React from "react"
import { Flex } from "../../styles/base-styles"

interface IconProps {
  width?: number
  height?: number
  direction?: "left" | "right" | "up" | "down"
  opacity?: number
  color?: string
  changeColorOnActive?: boolean
  isActive?: boolean
  activeColor?: string
  style?: React.CSSProperties
}

const ROTATIONS: Record<"left" | "right" | "up" | "down", number> = {
  left: 180,
  up: 270,
  right: 0,
  down: 90,
}

const CategoryChevron: React.FC<IconProps> = ({
  width,
  height,
  direction,
  changeColorOnActive,
  isActive,
  activeColor,
}) => {
  const defaultStrokeColor = "#242424"

  return direction === "left" ? (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_286_8961)">
        <rect
          x="16"
          y="16"
          width="16"
          height="16"
          rx="8"
          transform="rotate(180 16 16)"
          fill={
            changeColorOnActive && isActive && activeColor
              ? activeColor
              : defaultStrokeColor
          }
        />
        <path
          d="M7.41827 10.1816L5.091 7.99982M5.091 7.99982L7.41827 5.818M5.091 7.99982L10.9092 7.99982"
          stroke="#F4F4F4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_286_8961">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(16 16) rotate(180)"
          />
        </clipPath>
      </defs>
    </svg>
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_751_853)">
        <rect
          width="16"
          height="16"
          rx="8"
          fill={
            changeColorOnActive && isActive && activeColor
              ? activeColor
              : defaultStrokeColor
          }
        />
        <path
          d="M8.58173 5.81836L10.909 8.00018M10.909 8.00018L8.58173 10.182M10.909 8.00018H5.09082"
          stroke="#F4F4F4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_751_853">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

const Home: React.FC<IconProps> = ({ width, height }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 18 16"
    >
      <path
        id="Icon-Home"
        d="M9.2,19V13.353h3.6V19h4.5V11.471H20L11,3,2,11.471H4.7V19Z"
        transform="translate(-2 -3)"
        fill="#b2b2b2"
      />
    </svg>
  )
}

const Arrow: React.FC<IconProps> = ({
  width,
  height,
  direction,
  opacity,
  color,
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 8 10"
      transform={`rotate(${direction ? ROTATIONS[direction] : 0})`}
      style={{ transition: "0.2s" }}
    >
      <path
        id="Icon-Triangle"
        d="M8,5V15l8-5Z"
        transform="translate(-8 -5)"
        opacity={opacity || 1}
        fill={color || "#000000"}
      />
    </svg>
  )
}

const PlusMinus: React.FC<{
  isOpen: boolean
  size?: number
  color?: string
}> = ({ isOpen, size, color }) => {
  return (
    <Flex
      style={{ width: size || 10, height: size || 10, position: "relative" }}
    >
      <svg
        className="minus"
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="1.2"
        viewBox="0 0 10 1.2"
        style={{
          transition: "0.2s",
          top: "50%",
          left: "50%",
          position: "absolute",
          transform: `translate(-50%,-50%)`,
        }}
      >
        <path
          id="Icon-Minus-Orange"
          d="M15,12.2H5V11H15Z"
          transform="translate(-5 -11)"
          fill={color || "black"}
        />
      </svg>

      <svg
        className="minus"
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="1.2"
        viewBox="0 0 10 1.2"
        style={{
          transition: "0.2s",
          top: "50%",
          left: "50%",
          position: "absolute",
          transform: `translate(-50%, -50%) rotate(${isOpen ? 0 : 90}deg)`,
        }}
      >
        <path
          id="Icon-Minus-Orange"
          d="M15,12.2H5V11H15Z"
          transform="translate(-5 -11)"
          fill={color || "black"}
        />
      </svg>
    </Flex>
  )
}

const Search: React.FC<IconProps> = ({ width, height, style }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 16 16"
      style={style}
    >
      <g id="Icon_-_Search" data-name="Icon - Search" opacity="0.8">
        <rect
          id="Rectangle_693"
          data-name="Rectangle 693"
          width="16"
          height="16"
          fill="none"
        />
        <path
          id="Path_208"
          data-name="Path 208"
          d="M15.9,14.5l-3.3-3.3A6.847,6.847,0,0,0,14,7,6.957,6.957,0,0,0,7,0,6.957,6.957,0,0,0,0,7a6.957,6.957,0,0,0,7,7,6.847,6.847,0,0,0,4.2-1.4l3.3,3.3ZM2,7A4.951,4.951,0,0,1,7,2a4.951,4.951,0,0,1,5,5,4.951,4.951,0,0,1-5,5A4.951,4.951,0,0,1,2,7Z"
        />
      </g>
    </svg>
  )
}

const Hamburger: React.FC<IconProps> = ({ width, height, style }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 25 18"
      style={style}
    >
      <path
        id="Hamburger"
        d="M-6160,18V16h25v2Zm0-8V8h25v2Zm0-8V0h25V2Z"
        transform="translate(6160)"
        opacity="0.8"
      />
    </svg>
  )
}

const Close: React.FC<IconProps> = ({ width, height, style }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 15.943 15.943"
      style={style}
    >
      <path
        id="ic_clear_24px"
        d="M20.943,6.606,19.338,5l-6.366,6.366L6.606,5,5,6.606l6.366,6.366L5,19.338l1.606,1.606,6.366-6.366,6.366,6.366,1.606-1.606-6.366-6.366Z"
        transform="translate(-5 -5)"
        opacity="0.8"
      />
    </svg>
  )
}

const OpenNewWindow: React.FC<IconProps> = ({ width, height, style }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || "100%"}
      height={height || "100%"}
      viewBox="0 0 432 432"
      style={style}
    >
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g fill="#000000" fillRule="nonzero">
          <path d="M64,384 L288,384 C296.8,384 304,376.8 304,368 L304,304 L352,304 L352,368 C352,403.3 323.3,432 288,432 L64,432 C28.65,432 0,403.3 0,368 L0,144 C0,108.7 28.65,80 64,80 L128,80 L128,128 L64,128 C55.16,128 48,135.2 48,144 L48,368 C48,376.8 55.16,384 64,384 Z M80,64 C80,28.65 108.7,0 144,0 L368,0 C403.3,0 432,28.65 432,64 L432,288 C432,323.3 403.3,352 368,352 L144,352 C108.7,352 80,323.3 80,288 L80,64 Z M144,304 L368,304 C376.8,304 384,296.8 384,288 L384,64 C384,55.16 376.8,48 368,48 L144,48 C135.2,48 128,55.16 128,64 L128,288 C128,296.8 135.2,304 144,304 Z"></path>
        </g>
      </g>
    </svg>
  )
}

const XIcon: React.FC<IconProps> = ({ width, height, style }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
      imageRendering="optimizeQuality"
      fillRule="evenodd"
      clipRule="evenodd"
      viewBox="0 0 512 462.799"
      width={width || "100%"}
      height={height || "100%"}
      style={style}
    >
      <path
        fillRule="nonzero"
        d="M403.229 0h78.506L310.219 196.04 512 462.799H354.002L230.261 301.007 88.669 462.799h-78.56l183.455-209.683L0 0h161.999l111.856 147.88L403.229 0zm-27.556 415.805h43.505L138.363 44.527h-46.68l283.99 371.278z"
      />
    </svg>
  )
}

export const Icons = {
  CategoryChevron,
  Home,
  Arrow,
  Search,
  Hamburger,
  Close,
  PlusMinus,
  OpenNewWindow,
  XIcon,
}
